import React, { useState, useRef } from "react";
import {
  FiDownload,
  FiPaperclip,
  FiExternalLink,
  FiTrash,
  FiX,
} from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import Linkify from "linkify-react";
import { motion, AnimatePresence } from "framer-motion";

function MessageSenderCard({
  message,
  canDelete = false,
  onDeleteRequest,
  onLikeMessage,
}) {
  const [isSlideActive, setIsSlideActive] = useState(false);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const slideRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);
  const lastTapTime = useRef(0);

  //convert firebase timestamp to date
  const date = message.dateTime.toDate().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const handleFileDownload = (url, fileName) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Custom options for linkify
  const linkifyOptions = {
    defaultProtocol: "https",
    target: "_blank",
    rel: "noopener noreferrer",
    formatHref: {
      email: (href) => `mailto:${href}`,
      url: (href) => href,
    },
    render: {
      url: (props) => <a {...props} className="text-white underline" />,
    },
  };

  const handleDoubleClick = (e) => {
    // Prevent double click from triggering slide
    e.stopPropagation();

    if (!message.isDeleted && onLikeMessage) {
      onLikeMessage(message.id);
      // Show heart animation
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 1000);
    }
  };

  const handleMediaClick = (e) => {
    // Prevent event from bubbling up to parent elements which would trigger slide
    e.stopPropagation();

    // Prevent double-tap from happening when we want to view media
    lastTapTime.current = 0;

    // Only open modal if we're not currently sliding/dragging
    if (!isDragging.current) {
      setShowMediaModal(true);
    }
  };

  const closeMediaModal = () => {
    setShowMediaModal(false);
  };

  const handleTap = (e) => {
    // Don't process tap for liking if we're trying to slide for deletion
    if (isDragging.current) return;

    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTapTime.current;

    // Check if the tap happened within double-tap threshold (300ms)
    if (tapLength < 300 && tapLength > 0) {
      // Double tap detected
      if (!message.isDeleted && onLikeMessage) {
        onLikeMessage(message.id);
        // Show heart animation
        setShowLikeAnimation(true);
        setTimeout(() => setShowLikeAnimation(false), 1000);
      }

      // Reset last tap time to prevent triple-tap from triggering another like
      lastTapTime.current = 0;
    } else {
      // Set the timestamp for this tap
      lastTapTime.current = currentTime;
    }
  };

  // Touch and mouse event handlers for sliding
  const handleTouchStart = (e) => {
    if (!canDelete) return;
    isDragging.current = true;
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;

    // Also track for possible double-tap
    handleTap(e);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || !canDelete) return;
    currentX.current = e.touches[0].clientX;
    const diff = startX.current - currentX.current;

    // Only allow sliding to the left (positive values)
    if (diff > 0) {
      // Calculate how far we've dragged as a percentage (max 30%)
      const dragPercentage = Math.min(diff / 200, 0.3);

      if (slideRef.current) {
        slideRef.current.style.transform = `translateX(-${
          dragPercentage * 100
        }%)`;
      }

      // If dragged more than 15%, prepare to show delete button
      if (dragPercentage > 0.15 && !isSlideActive) {
        setIsSlideActive(true);
      } else if (dragPercentage <= 0.15 && isSlideActive) {
        setIsSlideActive(false);
      }

      // If we're dragging, this isn't a double-tap - reset the tap timer
      lastTapTime.current = 0;
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || !canDelete) return;
    isDragging.current = false;

    // Current translation value as percentage
    const currentTransform = slideRef.current
      ? Math.abs(
          parseFloat(
            slideRef.current.style.transform
              .replace("translateX(", "")
              .replace("%)", "")
          ) / 100
        )
      : 0;

    // If we've dragged enough to activate, keep it open
    if (currentTransform >= 0.15 && slideRef.current) {
      slideRef.current.style.transform = "translateX(-30%)";
      setIsSlideActive(true);
    } else if (slideRef.current) {
      // Otherwise snap back
      slideRef.current.style.transform = "translateX(0)";
      setIsSlideActive(false);
    }
  };

  // Mouse events for desktop
  const handleMouseDown = (e) => {
    if (!canDelete) return;
    isDragging.current = true;
    startX.current = e.clientX;
    currentX.current = startX.current;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || !canDelete) return;
    currentX.current = e.clientX;
    const diff = startX.current - currentX.current;

    // Only allow sliding to the left (positive values)
    if (diff > 0) {
      // Calculate how far we've dragged as a percentage (max 30%)
      const dragPercentage = Math.min(diff / 200, 0.3);

      if (slideRef.current) {
        slideRef.current.style.transform = `translateX(-${
          dragPercentage * 100
        }%)`;
      }

      // If dragged more than 15%, prepare to show delete button
      if (dragPercentage > 0.15 && !isSlideActive) {
        setIsSlideActive(true);
      } else if (dragPercentage <= 0.15 && isSlideActive) {
        setIsSlideActive(false);
      }
    }
  };

  const handleMouseUp = () => {
    if (!isDragging.current || !canDelete) return;
    isDragging.current = false;

    // Current translation value as percentage
    const currentTransform = slideRef.current
      ? Math.abs(
          parseFloat(
            slideRef.current.style.transform
              .replace("translateX(", "")
              .replace("%)", "")
          ) / 100
        )
      : 0;

    // If we've dragged enough to activate, keep it open
    if (currentTransform >= 0.15 && slideRef.current) {
      slideRef.current.style.transform = "translateX(-30%)";
      setIsSlideActive(true);
    } else if (slideRef.current) {
      // Otherwise snap back
      slideRef.current.style.transform = "translateX(0)";
      setIsSlideActive(false);
    }

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleDeleteClick = () => {
    if (onDeleteRequest) {
      onDeleteRequest();
      // Reset position after delete request
      if (slideRef.current) {
        slideRef.current.style.transform = "translateX(0)";
        setIsSlideActive(false);
      }
    }
  };

  const resetPosition = () => {
    if (slideRef.current) {
      slideRef.current.style.transform = "translateX(0)";
      setIsSlideActive(false);
    }
  };

  const likeCount = message.likes ? Object.keys(message.likes).length : 0;

  return (
    <div className="flex flex-col items-end mb-3 group animate-fade-in w-full">
      <div className="flex justify-end w-full">
        <div className="relative max-w-[85%]">
          {/* Delete button that appears behind the message */}
          {canDelete && isSlideActive && (
            <div
              className="absolute top-1/2 right-0 -translate-y-1/2 z-0 cursor-pointer bg-red-100 rounded-full p-1.5"
              onClick={handleDeleteClick}
              style={{ right: "16px" }}
            >
              <FiX className="w-4 h-4 text-red-500" />
            </div>
          )}

          {/* Message content that slides to reveal the delete button */}
          <div
            ref={slideRef}
            className="relative z-10 transition-transform duration-200"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            {/* Like animation that appears on double click */}
            {showLikeAnimation && (
              <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                <FaHeart className="text-red-500 animate-ping-once w-12 h-12" />
              </div>
            )}

            <div
              className={`rounded-lg rounded-tr-sm px-3 py-2 shadow-sm relative ${
                message.isDeleted
                  ? "bg-gray-200 text-gray-500 italic"
                  : "bg-[#8FCD00] text-white"
              }`}
              onClick={isSlideActive ? resetPosition : undefined}
              onDoubleClick={handleDoubleClick}
            >
              {/* If message is deleted */}
              {message.isDeleted ? (
                <p className="text-sm break-words">{message.message}</p>
              ) : (
                <>
                  {/* If message has an image */}
                  {message.fileType === "image" && message.fileUrl && (
                    <div
                      className="mb-2 bg-white rounded-md overflow-hidden cursor-pointer"
                      onClick={handleMediaClick}
                    >
                      <img
                        src={message.fileUrl}
                        alt="Shared image"
                        className="rounded-md max-w-full max-h-[300px] object-contain bg-white"
                      />
                    </div>
                  )}

                  {/* If message has a video */}
                  {message.fileType === "video" && message.fileUrl && (
                    <div
                      className="mb-2 bg-black rounded-md overflow-hidden cursor-pointer relative"
                      onClick={handleMediaClick}
                    >
                      <video
                        className="rounded-md max-w-full max-h-[300px] object-contain w-full"
                        controls={false}
                        muted
                        playsInline
                        poster={message.thumbnailUrl}
                      >
                        <source src={message.fileUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                          <span className="border-[8px] border-transparent border-l-white ml-1"></span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* If message has a file */}
                  {message.fileType === "file" && message.fileUrl && (
                    <div className="flex items-center gap-2 p-2 bg-white/10 rounded-md mb-2">
                      <div className="w-8 h-8 bg-white/20 rounded-md flex items-center justify-center">
                        <FiPaperclip className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {message.fileName}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileDownload(message.fileUrl, message.fileName);
                        }}
                        className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                      >
                        <FiDownload className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}

                  {/* If message has a link preview */}
                  {message.linkPreview && (
                    <div className="mb-2 border border-gray-200 rounded-md overflow-hidden bg-white">
                      <a
                        href={message.linkPreview.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:bg-gray-50 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {message.linkPreview.image && (
                          <div className="w-full h-[160px] bg-white border-b border-gray-100">
                            <img
                              src={message.linkPreview.image}
                              alt={message.linkPreview.title}
                              className="w-full h-full object-cover bg-white"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <div className="p-2 bg-white">
                          <div className="flex items-center text-xs text-gray-500 mb-1">
                            <FiExternalLink className="mr-1 w-3 h-3" />
                            <span>{message.linkPreview.siteName}</span>
                          </div>
                          <p className="font-medium text-sm mb-1 text-gray-800">
                            {message.linkPreview.title}
                          </p>
                          {message.linkPreview.description && (
                            <p className="text-xs text-gray-600 line-clamp-2">
                              {message.linkPreview.description}
                            </p>
                          )}
                        </div>
                      </a>
                    </div>
                  )}

                  {/* Message text content */}
                  {message.message && (
                    <p className="text-sm break-words">
                      <Linkify options={linkifyOptions}>
                        {message.message}
                      </Linkify>
                    </p>
                  )}
                </>
              )}

              {/* Like indicator */}
              {likeCount > 0 && (
                <div className="absolute bottom-0 right-0 transform translate-x-1/4 translate-y-1/4">
                  <div className="bg-white rounded-full p-1 shadow-sm flex items-center gap-1 border border-gray-200">
                    <FaHeart className="text-red-500 w-3 h-3" />
                    {likeCount > 1 && (
                      <span className="text-xs text-gray-700">{likeCount}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-1 text-right">{date}</div>
          </div>
        </div>
      </div>

      {/* Media Modal */}
      <AnimatePresence>
        {showMediaModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-3xl max-h-[90vh] relative"
            >
              <button
                className="absolute top-2 right-2 bg-white/20 rounded-full p-2 text-white"
                onClick={closeMediaModal}
              >
                <FiX className="w-5 h-5" />
              </button>

              {message.fileType === "image" && (
                <img
                  src={message.fileUrl}
                  alt="Shared media"
                  className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                />
              )}

              {message.fileType === "video" && (
                <video
                  src={message.fileUrl}
                  controls
                  autoPlay
                  className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                />
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MessageSenderCard;
